import React, { MouseEvent } from 'react';
import moment, { Moment } from 'moment';

import { TableCell, DayButton } from '../styles';

import { getMomentDate } from '../../helpers';
import { CustomDate } from '../CalendarShortcuts/CalendarShortcuts';
import { DateMask } from '../Calendar';
import CustomText from '../../../../Typography/CustomText/CustomText';
import { NewDatePickerProps } from '../../../../NewDatePicker/NewDatePicker';

export interface CalendarDaysProps {
  format?: NewDatePickerProps['format'];
  value: string;
  selectedDate: CustomDate;
  dateMask: DateMask;
  disableDates: (date: string) => boolean;
  monthStart: CustomDate;
  monthEnd: CustomDate;
  onDateClick: (e: MouseEvent, cloneDate: Moment) => void;
}

function CalendarDays(props: CalendarDaysProps) {
  const { format, value, selectedDate, dateMask, disableDates, monthStart, monthEnd, onDateClick } = props;

  const isDisabledDay = (day: Moment) => {
    const momentToDateString = day.format(dateMask);
    return disableDates(momentToDateString);
  };

  const isSelectedDay = (day: Moment) => {
    if (format) {
      return !isDisabledDay(day) && moment(day).isSame(selectedDate || moment(new Date()), 'day');
    }

    return (
      moment(day, dateMask).isSame(getMomentDate(selectedDate, dateMask), 'day') &&
      moment(value, dateMask).isValid() &&
      !isDisabledDay(day)
    );
  };

  const defineDayColor = (index: number, day: Moment) => {
    if (isSelectedDay(day)) {
      return 'white';
    }
    return index === 0 || index === 6 ? 'gray' : 'inherit';
  };

  const renderDayText = (index: number, day: Moment) => {
    if (moment(day).isSame(getMomentDate(moment(), dateMask), 'day')) {
      return (
        <CustomText as="div" size="small" color={defineDayColor(index, day)} weight="bold">
          {moment(day).format('D')}
        </CustomText>
      );
    }
    return (
      <CustomText as="div" size="xsmall" color={defineDayColor(index, day)}>
        {moment(day).format('D')}
      </CustomText>
    );
  };

  const startDate = getMomentDate(monthStart, dateMask).startOf('week');
  const endDate = getMomentDate(monthEnd, dateMask).endOf('week');

  const rows: any = [];

  let day = startDate;
  while (day <= endDate) {
    const days = Array.from({ length: 7 }).map((v, index) => {
      const cloneDay = day;
      day = moment(day).add(1, 'day');
      return (
        <TableCell key={cloneDay.toISOString()}>
          <DayButton
            type="button"
            onClick={e => onDateClick(e, cloneDay)}
            disabled={isDisabledDay(cloneDay)}
            selected={isSelectedDay(cloneDay)}
            data-date={cloneDay.format('YYYY-MM-DD')}
            aria-label={cloneDay.format('LL')}
          >
            {renderDayText(index, cloneDay)}
          </DayButton>
        </TableCell>
      );
    });
    rows.push(
      <tr key={day.toISOString()} data-testid={`${props['data-testid']}-calendardays`}>
        {days}
      </tr>,
    );
  }
  return rows;
}

export default CalendarDays;
